import React, { useCallback, useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Button, Space, Modal, message } from 'antd';
import { UserOutlined, MessageOutlined, TeamOutlined, LineChartOutlined } from '@ant-design/icons';
import { deleteMessage, deleteUser, getStats, type AdminMessage, type AdminStats, type AdminUser } from '../services/api';

const formatTime = (timestamp: number) => new Date(timestamp * 1000).toLocaleString('zh-CN');

const formatMessagePreview = (content: string, msgType?: number) => {
  if (msgType && msgType > 1) {
    try {
      const parsed = JSON.parse(content);
      const mediaLabelMap: Record<string, string> = {
        image: '[图片]',
        voice: '[语音]',
        video: '[视频]',
      };
      if (typeof parsed?.type === 'string' && mediaLabelMap[parsed.type]) {
        return mediaLabelMap[parsed.type];
      }
    } catch (error) {
      return content;
    }
  }
  return content;
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<AdminStats>({
    users: 0,
    messages: 0,
    friends: 0,
    active_users: 0,
    recent_users: [],
    recent_messages: [],
  });
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getStats();
      if (res.code !== 0) {
        message.error(res.message || '加载数据失败');
        return;
      }
      setStats(res.data);
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const userColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: '昵称', dataIndex: 'nickname', key: 'nickname' },
    { title: '手机号', dataIndex: 'phone', key: 'phone', render: (phone: string) => phone || '-' },
    { title: '状态', dataIndex: 'status', key: 'status', render: (status: number) => (
      <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? '正常' : '禁用'}</Tag>
    )},
    { title: '注册时间', dataIndex: 'created_at', key: 'created_at', render: (createdAt: number) => formatTime(createdAt) },
    { title: '操作', key: 'action', render: (_: unknown, record: AdminUser) => (
      <Space>
        <Button type="link" danger size="small" onClick={() => handleDeleteUser(record.id)}>删除</Button>
      </Space>
    )},
  ];

  const messageColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: '发送者', dataIndex: 'from_user', key: 'from_user' },
    { title: '接收者', dataIndex: 'to_user', key: 'to_user' },
    { title: '内容', dataIndex: 'content', key: 'content', ellipsis: true, render: (content: string, record: AdminMessage) => formatMessagePreview(content, record.msg_type) },
    { title: '时间', dataIndex: 'created_at', key: 'created_at', render: (createdAt: number) => formatTime(createdAt) },
    { title: '操作', key: 'action', render: (_: unknown, record: AdminMessage) => (
      <Button type="link" danger size="small" onClick={() => handleDeleteMessage(record.id)}>删除</Button>
    )},
  ];

  const handleDeleteUser = (id: number) => {
    Modal.confirm({
      title: '确认删除该用户吗？',
      onOk: async () => {
        const res = await deleteUser(id);
        if (res.code !== 0) {
          message.error(res.message || '删除失败');
          return;
        }
        message.success('删除成功');
        void loadData();
      },
    });
  };

  const handleDeleteMessage = (id: number) => {
    Modal.confirm({
      title: '确认删除该消息吗？',
      onOk: async () => {
        const res = await deleteMessage(id);
        if (res.code !== 0) {
          message.error(res.message || '删除失败');
          return;
        }
        message.success('删除成功');
        void loadData();
      },
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <h1>数据概览</h1>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="总用户数" value={stats.users} prefix={<UserOutlined />} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="消息总数" value={stats.messages} prefix={<MessageOutlined />} valueStyle={{ color: '#59D6AC' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="好友关系" value={stats.friends} prefix={<TeamOutlined />} valueStyle={{ color: '#722ed1' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="正常用户" value={stats.active_users} prefix={<LineChartOutlined />} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Card title="用户列表" style={{ marginBottom: 24 }}>
            <Table dataSource={stats.recent_users} columns={userColumns} rowKey="id" loading={loading} pagination={false} size="small" />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="最近消息">
            <Table dataSource={stats.recent_messages} columns={messageColumns} rowKey="id" loading={loading} pagination={false} size="small" />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
